// src/calculators/dividend.js — дивидентен калкулатор: двупосочен (бруто ↔
// нето), по фирма, с РЪЧНО разпределение по собственици.
//
// Правилата (потвърдени): данък 5% по подразбиране (редактируем);
//   бруто → данък = бруто × r;  нето = бруто − данък
//   нето  → бруто = нето / (1 − r);  данък = бруто − нето
// Дяловете (share_percent) са само СПРАВКА/предложение — разпределението е
// ръчно. ЕГН изобщо не се показва тук (само име и дял).

import { el, clear, field, input } from '../shared/dom.js';
import { money } from '../shared/format.js';
import { state } from '../shared/state.js';
import { toast } from '../shared/toast.js';
import { dividendForm } from '../protocols/dividends.js';
// Правното основание (задача 6/6) — цитатите са сверени в lex.bg.
import { withLaw, hintify } from './law.js';

const r2 = (x) => Math.round((Number(x) || 0) * 100) / 100;
// Резултатите с валута като цялата система (В.6).
const f2 = (x) => money(r2(x), 'EUR');

// ---- Чиста математика (тества се отделно) ---------------------------------

// От бруто: { gross, tax, net }. Данъкът се закръгля, нетото = бруто − данък
// (така бруто = данък + нето винаги, до стотинка).
export function fromGross(gross, rate) {
  const g = r2(gross);
  const tax = r2(g * (Number(rate) || 0) / 100);
  return { gross: g, tax, net: r2(g - tax) };
}

// От нето: брутото се закръгля, данъкът = бруто − нето (пак точен сбор).
// Ставка ≥ 100% е безсмислена — връщаме нули (не отрицателен данък).
export function fromNet(net, rate) {
  const n = r2(net);
  const rr = (Number(rate) || 0) / 100;
  if (rr >= 1) return { gross: 0, tax: 0, net: 0 };
  const gross = r2(n / (1 - rr));
  return { gross, tax: r2(gross - n), net: n };
}

// Сборове на редовете (без загуба на стотинки: сумираме закръглените редове).
export function sumRows(rows) {
  const t = { gross: 0, tax: 0, net: 0 };
  for (const row of rows || []) { t.gross = r2(t.gross + r2(row.gross)); t.tax = r2(t.tax + r2(row.tax)); t.net = r2(t.net + r2(row.net)); }
  return t;
}

// Предложение по дялове: разпределя общото БРУТО по share_percent, като
// последният ред поема остатъка от закръглянето (сборът = точно общото).
export function suggestByShares(totalGross, owners, rate) {
  const withPct = (owners || []).map((o) => ({ name: o.name, pct: Number(o.share_percent) || 0 }));
  const totalPct = withPct.reduce((s, o) => s + o.pct, 0);
  if (!totalPct) return null;
  const total = r2(totalGross);
  const out = [];
  // Никой ред не взима повече от остатъка (закръглянето нагоре иначе може
  // да остави отрицателна стотинка на последния); последният взима остатъка.
  let remaining = total;
  withPct.forEach((o, i) => {
    const gross = (i === withPct.length - 1) ? remaining : Math.min(r2(total * o.pct / totalPct), remaining);
    remaining = r2(remaining - gross);
    out.push(Object.assign({ name: o.name }, fromGross(gross, rate)));
  });
  return out;
}

// ---- Екранът --------------------------------------------------------------

export function buildDividendCalc() {
  const st = state();
  const companies = st.companies || [];
  let companyId = st.activeCompany || (companies[0] && companies[0].id) || null;
  let mode = 'gross';   // какво въвежда потребителят в редовете: 'gross' | 'net'
  let rows = [];        // { name, input: '' (низ от полето), gross, tax, net }

  const companySel = el('select', {}, ...companies.map((c) => el('option', { value: c.id, text: c.name, selected: Number(c.id) === Number(companyId) })));
  const rateI = input({ type: 'number', step: '0.1', min: '0', max: '99', value: 5, style: 'max-width:110px' });
  const totalI = input({ type: 'number', step: '0.01', min: '0', placeholder: '0.00' });
  const totalModeSel = el('select', {},
    el('option', { value: 'gross', text: 'Въвеждам БРУТО (преди данъка)' }),
    el('option', { value: 'net', text: 'Въвеждам НЕТО (за получаване)' }));
  const totalOut = el('div', { class: 'calc-result' });

  const rowModeSel = el('select', {},
    el('option', { value: 'gross', text: 'Въвеждам бруто на всеки' }),
    el('option', { value: 'net', text: 'Въвеждам нето на всеки' }));
  const tableBox = el('div', {});
  const sumOut = el('div', { class: 'calc-result' });

  const rate = () => Number(rateI.value) || 0;

  function ownersOf() {
    const c = companies.find((x) => Number(x.id) === Number(companyId));
    return (c && Array.isArray(c.shareholders)) ? c.shareholders : [];
  }

  function loadOwners() {
    rows = ownersOf().map((s) => ({ name: s.name, share_percent: s.share_percent, input: '', gross: 0, tax: 0, net: 0 }));
    if (!rows.length) rows = [{ name: '', share_percent: null, input: '', gross: 0, tax: 0, net: 0 }];
  }

  // Горният блок: общата сума в двете посоки.
  function recomputeTotal() {
    clear(totalOut);
    const v = Number(totalI.value) || 0;
    const t = totalModeSel.value === 'net' ? fromNet(v, rate()) : fromGross(v, rate());
    [['Бруто (преди данъка)', t.gross], ['Данък дивидент ' + rate() + '%', t.tax]].forEach(([l, x]) =>
      totalOut.appendChild(el('div', { class: 'calc-line' }, el('span', { text: l }), el('span', { text: f2(x) }))));
    totalOut.appendChild(el('div', { class: 'calc-line big' }, el('span', { text: 'Нето за получаване' }), el('span', { text: f2(t.net) })));
  }

  function recomputeRow(row) {
    const v = Number(row.input) || 0;
    const t = mode === 'net' ? fromNet(v, rate()) : fromGross(v, rate());
    row.gross = t.gross; row.tax = t.tax; row.net = t.net;
  }

  function drawRows() {
    clear(tableBox);
    const tb = el('tbody');
    rows.forEach((row, i) => {
      recomputeRow(row);
      const nameI = input({ value: row.name, placeholder: 'Име на собственика' });
      nameI.addEventListener('input', (e) => { row.name = e.target.value; });
      const amtI = input({ type: 'number', step: '0.01', min: '0', value: row.input, placeholder: mode === 'net' ? 'нето' : 'бруто' });
      amtI.addEventListener('input', (e) => { row.input = e.target.value; recomputeRow(row); refreshComputedCells(); });
      row._cells = {
        gross: el('td', { class: 'num', text: f2(row.gross) }),
        tax: el('td', { class: 'num', text: f2(row.tax) }),
        net: el('td', { class: 'num', text: f2(row.net) }),
      };
      tb.appendChild(el('tr', {},
        el('td', {}, nameI, row.share_percent != null ? el('div', { style: 'font-size:11px;color:var(--muted)', text: 'дял ' + row.share_percent + '% (справка)' }) : null),
        el('td', {}, amtI),
        row._cells.gross, row._cells.tax, row._cells.net,
        el('td', {}, el('button', { class: 'btn sm ghost', type: 'button', text: '×', title: 'Махни реда', onClick: () => { rows.splice(i, 1); drawRows(); } }))
      ));
    });
    tableBox.appendChild(el('table', {}, el('thead', {}, el('tr', {},
      el('th', { text: 'Собственик' }), el('th', { text: mode === 'net' ? 'Нето (въвеждаш)' : 'Бруто (въвеждаш)' }),
      el('th', { class: 'num', text: 'Бруто' }), el('th', { class: 'num', text: 'Данък' }), el('th', { class: 'num', text: 'Нето' }), el('th', {}))), tb));
    tableBox.appendChild(el('div', { style: 'display:flex;gap:8px;margin-top:8px;flex-wrap:wrap' },
      el('button', { class: 'btn sm ghost', type: 'button', text: '+ Добави ред', onClick: () => { rows.push({ name: '', share_percent: null, input: '', gross: 0, tax: 0, net: 0 }); drawRows(); } }),
      suggestBtn));
    refreshComputedCells();
  }

  function refreshComputedCells() {
    rows.forEach((row) => {
      if (!row._cells) return;
      row._cells.gross.textContent = f2(row.gross);
      row._cells.tax.textContent = f2(row.tax);
      row._cells.net.textContent = f2(row.net);
    });
    clear(sumOut);
    const t = sumRows(rows);
    sumOut.appendChild(el('div', { class: 'calc-line' }, el('span', { text: 'Общо бруто' }), el('span', { text: f2(t.gross) })));
    sumOut.appendChild(el('div', { class: 'calc-line' }, el('span', { text: 'Общо данък' }), el('span', { text: f2(t.tax) })));
    sumOut.appendChild(el('div', { class: 'calc-line big' }, el('span', { text: 'Общо нето за изплащане' }), el('span', { text: f2(t.net) })));
  }

  // Предложение по дяловете (само попълва полетата — потребителят може да пипа).
  // Пълни БРУТО стойности (и превключва редовете на бруто): нето→бруто не е
  // обратимо след закръгляне и в режим нето сборът би губил стотинки.
  const suggestBtn = el('button', {
    class: 'btn sm ghost', type: 'button', text: 'Предложи по дяловете',
    title: 'Разпределя общото БРУТО отгоре по дял % (само предложение)',
    onClick: () => {
      const t = totalModeSel.value === 'net' ? fromNet(Number(totalI.value) || 0, rate()) : fromGross(Number(totalI.value) || 0, rate());
      const suggested = suggestByShares(t.gross, rows, rate());
      if (!suggested) { toast('Никой ред няма дял % — попълни дяловете или въведи сумите ръчно.', true); return; }
      mode = 'gross';
      rowModeSel.value = 'gross';
      rows.forEach((row, i) => {
        if (!suggested[i]) return;
        row.input = String(suggested[i].gross);
        recomputeRow(row);
      });
      drawRows();
    },
  });

  companySel.addEventListener('change', (e) => { companyId = Number(e.target.value); loadOwners(); drawRows(); });
  rateI.addEventListener('input', () => { rows.forEach(recomputeRow); refreshComputedCells(); recomputeTotal(); });
  totalI.addEventListener('input', recomputeTotal);
  totalModeSel.addEventListener('change', recomputeTotal);
  rowModeSel.addEventListener('change', (e) => { mode = e.target.value; drawRows(); });

  loadOwners();

  const panel = el('div', { class: 'panel calc-panel calc-dividend' },
    el('h3', { class: 'calc-title', text: 'Дивидент — данък и разпределение' }),
    el('div', { class: 'calc-fields' },
      field('Фирма', companySel),
      el('div', { class: 'field-row' }, field('Обща сума', totalI), field('Каква е сумата', totalModeSel),
        hintify(field('Ставка %', rateI), 'чл. 46, ал. 3 ЗДДФЛ', '5 на сто за дивиденти и ликвидационни дялове (чл. 38, ал. 1); основата е БРУТНАТА сума по решението (чл. 38, ал. 2).'))),
    totalOut,
    el('h4', { style: 'margin:18px 0 6px', text: 'Разпределение по собственици (ръчно)' }),
    el('p', { style: 'font-size:13px;color:var(--muted);margin:0 0 8px', text: 'Въведи сумата на всеки собственик сам — редът смята останалото. Дяловете са само справка.' }),
    field('Какво въвеждаш в редовете', rowModeSel),
    tableBox,
    sumOut
  );
  drawRows();
  recomputeTotal();

  // Мястото, където Фаза 4/5 закача „към документ" (мостът чете оттук).
  panel._dividend = {
    rows: () => rows.filter((r) => (r.name || '').trim() || r.gross).map((r) => ({ name: r.name, share_percent: r.share_percent, gross: r.gross, tax: r.tax, net: r.net })),
    totals: () => sumRows(rows),
    companyId: () => companyId,
    rate,
  };
  const wrapped = withLaw(panel, {
    when: [
      'Общото събрание взема решение за разпределяне на дивидент към собствениците — физически лица: 5 % окончателен данък.',
      'Срокът за внасяне и деклариране тече от тримесечието на РЕШЕНИЕТО, не от плащането.',
      'Дивидент към юридическо лице: чуждестранно ЮЛ или местно ЮЛ-нетърговец → 5 % по чл. 194 ЗКПО; местно ЮЛ-търговец не се облага при източника.',
      'Разпределението по собственици е ръчно — дяловете са само справка, а протоколът се издава от бутона под панела.',
    ],
    quotes: [
      { ref: 'чл. 38, ал. 1 ЗДДФЛ (изм. ДВ, бр. 99/2011)', text: 'С окончателен данък се облагат облагаемите доходи от: 1. дивиденти в полза на едноличен търговец; 2. дивиденти и ликвидационни дялове в полза на: а) местно или чуждестранно физическо лице от източник в България; б) местно физическо лице от източник в чужбина.' },
      { ref: 'чл. 38, ал. 2 ЗДДФЛ', text: 'Окончателният данък за доходите от дивиденти се определя върху брутната сума, определена с решението за разпределяне на дивидент.' },
      { ref: 'чл. 46, ал. 3 ЗДДФЛ', text: 'Ставката на данъка е 5 на сто за доходите по чл. 38, ал. 1.' },
      { ref: 'чл. 65, ал. 2 ЗДДФЛ (изм. ДВ, бр. 94/2012)', text: 'Данъкът по чл. 46 за доходите по чл. 38, ал. 2 се удържа и внася от предприятието – платец на дохода, в срок до края на месеца, следващ тримесечието, през което е взето решението за разпределяне на дивидент.' },
      { ref: 'чл. 194, ал. 1 и чл. 200, ал. 1 ЗКПО', text: 'С данък при източника се облагат дивидентите и ликвидационните дялове, разпределени (персонифицирани) от местни юридически лица в полза на: 1. чуждестранни юридически лица…; 2. местни юридически лица, които не са търговци, включително на общини. … Данъчната ставка на данъка върху доходите по чл. 194 е 5 на сто.' },
    ],
  });
  // „Към документ" (dividendToProtocol) чете _dividend от върнатия елемент.
  wrapped._dividend = panel._dividend;
  return wrapped;
}

// „Към документ" (закача се от регистъра като toDocument.run): отваря
// протоколната форма ПРЕДПОПЪЛНЕНА; записът става чак след „Запази".
// ЕГН не се праща — сървърът го попълва от собствениците по име.
export function dividendToProtocol(panel) {
  const d = panel._dividend;
  const rws = d.rows().filter((r) => (r.name || '').trim());
  if (!rws.length) { toast('Въведи поне един ред с име и сума.', true); return; }
  dividendForm({
    company_id: d.companyId(),
    company_type: rws.length === 1 ? 'eood' : 'ood',
    tax_rate: d.rate(),
    // Само именуваните редове влизат в протокола — и общата печалба е
    // ТЕХНИЯТ сбор (иначе ред със сума без име надува юридическия текст).
    total_profit: sumRows(rws).gross,
    partners: rws.map((r) => ({ name: r.name, egn: '', percent: r.share_percent, gross: r.gross })),
  });
}
